import { useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Cpu, Sparkles, Play, Volume2, VolumeX } from "lucide-react";
import { scrollToId } from "../../hooks/useLenis";
import { useLang } from "../../i18n/LanguageContext";

export const Laboratory = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });
  const { t } = useLang();
  const [muted, setMuted] = useState(true);

  const STATS = [
    { k: "10", label: t("lab.statModules") },
    { k: "∞", label: t("lab.statClones") },
    { k: "4K", label: t("lab.statRender") },
  ];

  const SoundBtn = () => (
    <button
      data-testid="lab-sound-toggle"
      onClick={() => setMuted((m) => !m)}
      title={muted ? "Activer le son" : "Couper le son"}
      className="absolute bottom-4 right-4 flex h-10 w-10 items-center justify-center rounded-full border border-blue-400/40 bg-[#0B1220]/80 text-blue-100 backdrop-blur-md transition-all duration-300 hover:bg-blue-500/30"
    >
      {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
    </button>
  );

  return (
    <section id="laboratory" data-testid="laboratory-section" className="relative overflow-hidden py-28 lg:py-36">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_40%,rgba(37,99,235,0.12),transparent_55%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_85%_70%,rgba(37,99,235,0.08),transparent_50%)]" />
      <div ref={ref} className="relative mx-auto grid max-w-[1400px] grid-cols-1 items-center gap-12 px-6 lg:grid-cols-2 lg:gap-16 lg:px-10">
        {/* Left — copy */}
        <div className="relative">
          <motion.span initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}} className="font-mono text-xs uppercase tracking-[0.35em] text-blue-400">
            [ {t("lab.tag")} ]
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 30 }} animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="mt-4 font-display text-4xl font-800 tracking-tighter text-white sm:text-5xl lg:text-6xl"
          >
            {t("lab.title1")} <span className="text-glow text-blue-300">{t("lab.title2")}</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="mt-5 max-w-xl font-body text-base leading-relaxed text-[#CBD5E1] sm:text-lg"
          >
            {t("lab.desc")}
          </motion.p>

          <div className="mt-8 flex flex-wrap gap-8">
            {STATS.map((s, i) => (
              <motion.div key={s.label} initial={{ opacity: 0, y: 16 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6, delay: 0.25 + i * 0.08 }}>
                <div className="font-display text-3xl font-800 tracking-tighter text-white">{s.k}</div>
                <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-[#8AA0C0]">{s.label}</div>
              </motion.div>
            ))}
          </div>

          <motion.div initial={{ opacity: 0, y: 16 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6, delay: 0.5 }} className="mt-9 flex flex-wrap gap-3">
            <button data-testid="lab-cta-tools" onClick={() => scrollToId("cockpit")} className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-6 py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] text-white cta-glow transition-all duration-300 hover:scale-[1.02] hover:bg-blue-500">
              <Cpu size={15} /> {t("lab.ctaTools")}
            </button>
            <button data-testid="lab-cta-video" onClick={() => scrollToId("cockpit")} className="inline-flex items-center gap-2 rounded-full border border-blue-400/40 bg-blue-500/10 px-6 py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] text-blue-100 transition-all duration-300 hover:bg-blue-500/20">
              <Sparkles size={15} /> {t("lab.ctaVideo")}
            </button>
          </motion.div>
        </div>

        {/* Right — lab videos (enlarged, with sound toggle) */}
        <div className="relative flex flex-col items-center gap-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 30 }} animate={inView ? { opacity: 1, scale: 1, y: 0 } : {}}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
            className="relative w-full"
          >
            <div className="absolute left-1/2 top-1/2 h-[440px] w-[440px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-blue-500/20 blur-[100px]" />
            <div className="relative flex flex-col gap-6">
              <div className="group relative overflow-hidden rounded-3xl border border-blue-500/25 bg-[#0B1220]/60 backdrop-blur-xl shadow-[0_0_50px_rgba(37,99,235,0.2)]">
                <video
                  data-testid="lab-video-main"
                  src="/assets/lab-video.mp4"
                  autoPlay muted={muted} loop playsInline
                  className="aspect-video w-full object-cover"
                />
                <span className="absolute left-4 top-4 flex items-center gap-1.5 rounded-full border border-blue-400/30 bg-[#0B1220]/80 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-blue-200">
                  <Play size={11} /> {t("lab.tag")}
                </span>
                <SoundBtn />
              </div>
              <div className="relative overflow-hidden rounded-3xl border border-blue-500/20 bg-[#0B1220]/60 backdrop-blur-xl shadow-[0_0_50px_rgba(37,99,235,0.15)]">
                <video
                  data-testid="lab-video-secondary"
                  src="/assets/lab-showcase.mp4"
                  autoPlay muted={muted} loop playsInline
                  className="aspect-video w-full object-cover"
                />
                <SoundBtn />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
